<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuizterStore } from '../../stores/quizterStore';
import { addLeaderboardPositions } from '../../util/sortResults';
import { usePodiumAnimation } from '../../composables/usePodiumAnimation';
import type { MatrixPlayer } from '../../util/answerRecapMatrix';
import Card from '../../components/Card.vue';
import NextButton from '../../components/NextButton.vue';
import Podium, { type PodiumPlayer } from '../../components/host/Podium.vue';
import LeaderBoard from '../../components/host/LeaderBoard.vue';
import AnswerRecapMatrix from '../../components/host/AnswerRecapMatrix.vue';
import useLocale from '../../../composables/use-locale';

const quizterStore = useQuizterStore();
const {
  results,
  joinedPlayers,
  returnParams,
  gameOptions,
  playedQuestions,
  answersByQuestion,
  playerNames,
  isLoadingAnswerRecap,
} = storeToRefs(quizterStore);
const { t } = useLocale();

const showAnswerRecapMatrix = ref(false);

// The matrix only ever arrives in reply to the host's own request, so its arrival opens it.
watch(playedQuestions, () => {
  showAnswerRecapMatrix.value = true;
});

// Get top 3 players with positions
const topPlayers = computed<PodiumPlayer[]>(() => {
  const sorted = addLeaderboardPositions(results.value);
  return sorted.slice(0, 3).map((result) => ({
    position: result.currentPosition,
    playerId: result.player.toString(),
    score: result.totalPoint,
    avatar: joinedPlayers.value?.find((jp) => jp.id.toString() === result.player.toString())!.avatar.image,
  }));
});

// Prepare data for animation composable
const animationPlayers = computed(() =>
  topPlayers.value.map((p) => ({
    position: p.position,
    score: p.score,
  })),
);

const { podiumsVisible, animatedScores, allRevealed } = usePodiumAnimation(animationPlayers.value);

// The join-order label is the fallback for a signed-in player whose account carried no
// name, which Userville can fail to supply.
const matrixPlayers = computed<MatrixPlayer[]>(() =>
  joinedPlayers.value.map((player, index) => ({
    id: player.id,
    label: playerNames.value[player.id] ?? t('results.answer_recap.player_label', { number: index + 1 }),
    avatar: player.avatar?.image,
  })),
);

// Without showAnswerRecap nobody signed in, so there is no roster to attribute answers to.
const hasAnswerRecapData = computed(() => !!gameOptions.value.showAnswerRecap && matrixPlayers.value.length > 0);
</script>

<template>
  <div class="d-flex flex-column align-center">
    <AnswerRecapMatrix
      v-if="showAnswerRecapMatrix"
      :questions="playedQuestions"
      :answers-by-question="answersByQuestion"
      :players="matrixPlayers"
      @close="showAnswerRecapMatrix = false"
    />

    <!--
      Hidden rather than destroyed: the LeaderBoard's score count-up runs on mount and would
      replay every time the matrix closes.
    -->
    <div :class="showAnswerRecapMatrix ? 'd-none' : 'd-flex flex-column align-center ga-6'">
      <Card :content-max-width="800">
        <div class="d-flex flex-column align-center justify-center pa-6">
          <!-- Podium: only with timer -->
          <Podium
            v-if="gameOptions.useTimer"
            :top-players="topPlayers"
            :podiums-visible="podiumsVisible"
            :animated-scores="animatedScores"
            :compact="allRevealed"
          />

          <!-- Game Over header: only without timer -->
          <h1 v-else class="chalkboard-font text-white text-center text-h3 mb-6">
            {{ t('end_of_game.title') }}
          </h1>

          <!-- LeaderBoard: always shown, zoomed only with podium -->
          <Transition name="fade">
            <div
              v-if="!gameOptions.useTimer || allRevealed"
              class="w-100 mt-6"
              :class="{ 'leaderboard-wrapper': gameOptions.useTimer }"
            >
              <LeaderBoard :results="results" />
            </div>
          </Transition>
        </div>
      </Card>

      <Transition name="fade">
        <!--
          Outlined rather than a NextButton: quieter for a secondary view, and
          NextButton binds the space bar globally — two would both fire at once.
        -->
        <v-btn
          v-if="hasAnswerRecapData && (!gameOptions.useTimer || allRevealed)"
          variant="outlined"
          color="white"
          class="text-none text-body-1 results-button chalkduster-font"
          data-testid="quizter-show-answer-recap-button"
          :loading="isLoadingAnswerRecap"
          @click="quizterStore.requestAnswerRecapMatrix"
        >
          {{ t('results.answer_recap.open_button') }}
        </v-btn>
      </Transition>

      <!-- Return Button -->
      <Transition name="fade">
        <NextButton
          v-if="returnParams && (!gameOptions.useTimer || allRevealed)"
          @click="quizterStore.reset"
          :href="returnParams.url"
          data-testid="quizter-return-button"
        >
          {{ returnParams.text }}
        </NextButton>
      </Transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Fade transition for return button and leaderboard
.fade-enter-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from {
  opacity: 0;
}

.leaderboard-wrapper {
  zoom: 0.6;
}

.results-button {
  // 3px matches the outlined cards on the quizter home page.
  border-width: 3px;
}
</style>
